import { Component, inject, signal, computed, effect, HostListener, ElementRef, ViewChild, DestroyRef, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MsalService } from '@azure/msal-angular';
import { SearchCasesComponent } from '../search-cases/search-cases';
import { SearchResultsComponent } from '../search-results/search-results';
import { EditFieldsComponent } from '../edit-fields/edit-fields';
import { CaseService } from '../../services/case-service/case.service';
import { LookUpService } from '../../services/lookup-service/lookup.service';
import { ActionService } from '../../services/action-service/action.service';
import { Loader } from '../../shared-components/loader/loader';
import { LoginService } from '../../services/login-service/login.service';
import { ConfigService } from '../../core/config.service';

@Component({
  selector: 'app-workspace',
  imports: [
    CommonModule,
    SearchCasesComponent,
    SearchResultsComponent,
    EditFieldsComponent,
    Loader
  ],
  templateUrl: `./workspace.html`,
  styleUrls: [`./workspace.scss`]
})
export class WorkspaceComponent implements OnInit, OnDestroy {
  readonly caseService = inject(CaseService);
  private readonly authService = inject(MsalService);
  private readonly elementRef = inject(ElementRef);
  private readonly destroyRef = inject(DestroyRef);
  readonly actionService = inject(ActionService);
  protected readonly footerText =
    inject(ConfigService).get().footerText ?? 'Copyright © 2026. All right reserved.';
  protected readonly helpDocumentationUrl = inject(ConfigService).get().helpDocumentationUrl;
  protected readonly tooltipTexts = inject(ConfigService).getTooltipTexts();

  protected readonly bannerText = this.caseService.bannerText;
  private readonly loginService = inject(LoginService);
  protected readonly username = this.loginService.username;
  protected readonly userInitial = computed(() => this.username().charAt(0));
  protected readonly menuOpen = signal(false);

  protected readonly generatedLetter = this.actionService.generatedLetter;

  lookUpService = inject(LookUpService);

  // Anchors for mobile auto-scroll
  @ViewChild('searchResultsAnchor') private searchResultsAnchor?: ElementRef<HTMLElement>;
  @ViewChild('bannerAnchor') private bannerAnchor?: ElementRef<HTMLElement>;

  private readonly MOBILE_BREAKPOINT = 992;
  private wasSearching = false;

  // FIX: Track pending scroll timeouts
  private pendingScrollTimeouts: ReturnType<typeof setTimeout>[] = [];
  
  // FIX: Track document click listener for cleanup
  private documentClickListener: ((event: MouseEvent) => void) | null = null;

  constructor() {
    effect(() => {
      const isSearching = this.caseService.isSearching();
      const results = this.caseService.searchResults();

      if (this.wasSearching && !isSearching && results.length > 0 && this.isMobile()) {
        this.scrollToElement(this.searchResultsAnchor);
      }

      this.wasSearching = isSearching;
    });

    // FIX: Use effect with proper cleanup
    // Scroll UP to banner once a letter is generated (mobile only)
    effect(() => {
      const letter = this.generatedLetter();

      if (letter && this.isMobile()) {
        
        this.scrollToElement(this.bannerAnchor);
      }
    });
  }

  ngOnInit(): void {
    // FIX: Setup manual document click listener for cleanup in ngOnDestroy
    this.documentClickListener = (event: MouseEvent) => this.onDocumentClick(event);
    document.addEventListener('click', this.documentClickListener);
  }

  
  ngOnDestroy(): void {
    this.pendingScrollTimeouts.forEach(timeout => clearTimeout(timeout));
    this.pendingScrollTimeouts = [];

    if (this.documentClickListener) {
      document.removeEventListener('click', this.documentClickListener);
      this.documentClickListener = null;
    }

    // Clear ViewChild references
    this.searchResultsAnchor = undefined;
    this.bannerAnchor = undefined;

    // Reset state
    this.wasSearching = false;
  }

  toggleMenu(): void {
    this.menuOpen.update((open) => !open);
  }

  closeMenu(): void {
    this.menuOpen.set(false);
  }

  // FIX: Manual implementation instead of @HostListener to allow cleanup
  private onDocumentClick(event: MouseEvent): void {
    if (!this.menuOpen()) return;
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.closeMenu();
    }
  }

  async copyPath(path: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(path);
      this.caseService.showToast('Path copied to clipboard.', 'success');
    } catch {
      this.caseService.showToast('Could not copy path.', 'error');
    }
  }

  openLetter(): void {
    this.actionService.openGeneratedLetter();
  }

  closeBanner(): void {
    this.actionService.closeBanner();
  }

  logout(): void {
    this.pendingScrollTimeouts.forEach(timeout => clearTimeout(timeout));
    this.pendingScrollTimeouts = [];

    localStorage.removeItem('tokenId');
    this.loginService.clearUsername();

    const accounts = this.authService.instance.getAllAccounts();

    let activeAccount = this.authService.instance.getActiveAccount();
    if (!activeAccount && accounts.length > 0) {
      activeAccount = accounts[0];
      this.authService.instance.setActiveAccount(activeAccount);
    }

    this.authService.instance.logoutRedirect({
      account: activeAccount,
      postLogoutRedirectUri: `${window.location.origin}/logout`,
    });
  }

  private isMobile(): boolean {
    return window.matchMedia(`(max-width: ${this.MOBILE_BREAKPOINT}px)`).matches;
  }

  private scrollToElement(ref?: ElementRef<HTMLElement>): void {
    if (!ref?.nativeElement) {
      return;
    }

    // FIX: Track timeout for cleanup
    const timeoutId = setTimeout(() => {
      if (ref?.nativeElement && typeof ref.nativeElement.scrollIntoView === 'function') {
        ref.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);

    this.pendingScrollTimeouts.push(timeoutId);
  }
}